import { CSSObject, SerializedStyles, ArrayInterpolation } from '@emotion/react';
import {
  ListAppointmentsResponse,
  ListCalendarExceptionReply,
} from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { OfficeHoursDaySchedule } from '@weave/schema-gen-ts/dist/schemas/schedule/settings/v2/settings.pb';
import { Operatory } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/operatory.pb';
import { RecurringRules } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/schedule.pb';
import { SortOrder } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/sort.pb';
import { UseQueryResult } from 'react-query';
import { PersonTypes } from '@frontend/api-person';
import { ScheduleTypes } from '@frontend/api-schedule';
import { ExtendedAppointment, ScheduleRequestEvent } from '@frontend/schedule-helpers';

export type RenderAppointmentInfoTabContent = (
  tabName: 'notes' | 'forms' | 'payments',
  details: {
    locationId: string;
    apptReferenceId: string;
    personInfo?: PersonTypes.Person;
    isPaymentTabActive?: boolean;
  }
) => React.ReactNode;

// SP-TODO: re-export to avoid changing import paths in dependent files, need to clean up later
export { type ExtendedAppointment, type ScheduleRequestEvent };

export type OpertoriesV3SortBy = Capitalize<keyof Operatory>;
export type OpertoriesV3SortByAvailable = Extract<
  OpertoriesV3SortBy,
  'DisplayName' | 'ExternalName' | 'Hidden' | 'SourceId'
>;
export type OpertoriesV3TableColumnIds =
  | { id: OpertoriesV3SortByAvailable; disableSortBy?: false }
  | { id: OpertoriesV3SortBy; disableSortBy: true };

export const OPERTORIES_V3_SORT_BY: { [key in OpertoriesV3SortByAvailable]: OpertoriesV3SortByAvailable } = {
  DisplayName: 'DisplayName',
  ExternalName: 'ExternalName',
  Hidden: 'Hidden',
  SourceId: 'SourceId',
} as const;

export const isOpertoriesV3SortByAvailable = (id: string): id is OpertoriesV3SortByAvailable => {
  return id in OPERTORIES_V3_SORT_BY;
};

export type PageConfig = {
  page: number;
  limit: number;
  searchValue?: string;
  sortBy?: OpertoriesV3SortByAvailable;
  sortDir?: SortOrder;
};

export type OfficeHoursDayScheduleType = OfficeHoursDaySchedule & {
  providerId?: string;
};

export type CalendarViewRefetchMethodContext = {
  refetchProvidersExceptions: UseQueryResult<ListCalendarExceptionReply>['refetch'];
  refetchOfficeExceptions: UseQueryResult<ListCalendarExceptionReply>['refetch'];
  refetchAppointments: UseQueryResult<ListAppointmentsResponse>['refetch'];
};

export type ScheduleActionsContainerTypes = {
  setHideTabs: (val: boolean) => void;
  hideTabs: boolean;
  selectedLocationId: string | null;
  setSelectedLocationId: (val: string | null) => void;
  closeModal?: () => void;
  selectedDate: string;
  setSelectedDate: (val: string) => void;
  isSmallModeWindow?: boolean;
};

export type ScheduleAlertsContextType = {
  refetchScheduleAlerts: () => void;
  clearAlert: (id: string, locationId?: string) => void;
  setSelectedLocationId: (locationId: string) => void;
};

type ScheduleHoursType = 'working_hours' | 'break_hours';

export type ConversionType = 'rrule_to_schedule_hours' | 'schedule_hours_to_rrule';

export type ScheduleHoursSessionMap = {
  days: string[];
  startTime: string;
  endTime: string;
  duration: number;
  type: ScheduleHoursType;
};

export type AddEntryParamsType = {
  scheduleHoursMap: Map<string, ScheduleHoursSessionMap>;
  dayAbbreviation: string;
  startTime: string;
  endTime: string;
  duration: number;
  type: ScheduleHoursType;
};

export type ScheduleHours = Omit<RecurringRules, 'override'>;

export enum ExceptionStatus {
  Available = 'Available',
  Unavailable = 'Unavailable',
}

export type ExceptionListItem = {
  id: string;
  name: string;
  status: ExceptionStatus;
  startDateTime?: string;
  endDateTime?: string;
  duration: number;
};

export type SelectedFiltersType = {
  practitionerIds: string[];
  appointmentTypeIds: string[];
  confirmationStatus: string;
};

export enum ExceptionTableFilter {
  Upcoming = 'Upcoming',
  Past = 'Past',
  All = 'All Exceptions',
}

export type ExtendedProvider = ScheduleTypes.SchemaProvider & { hidden?: boolean };

export type CalendarEventContextType = {
  selectedLocationIds: string[];
  providersList: ExtendedProvider[];
};

export type ListOrderIdMap = Record<string, number>;

export type EmotionStyle =
  | SerializedStyles
  | ArrayInterpolation<any>
  | CSSObject
  | null
  | undefined
  | boolean
  | number
  | string;
